/**
 * NDE results and their NCR dispositions. CR-2026-NDE-NCR (2026-10-15). Server: /api/qdp-links/nde-records, /nde/:id/ncr.
 * Every NDE result on a job's welds, with the NCR that dispositions a rejected one. The NCR is linked while it is open and
 * must cover the welded mark. A rejection clears (Gate 6 and the quality data package) only by a later accepted
 * re-inspection by the same method, or by its linked QA NCR dispositioned use-as-is and closed after the link. A system
 * NCR clears a rejection only through a re-inspection. The link is made once, with a reason, and is never changed.
 * Recording, accepting and rejecting NDE results is not on this page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const day = (d) => (d ? String(d).slice(0, 10) : "");

/** How a result stands for Gate 6 and the package. Mirrors the server rule; the server decides. */
export function ndeStanding(r, all) {
  const closedAfterLink = (n) => !!n && (n.register === "NCR_REPORTS" ? n.status === "closed" : n.status === "CLOSED")
    && !!n.closedAt && !!n.linkedAt && new Date(n.closedAt) > new Date(n.linkedAt);
  if (r.status === "voided") {
    if (r.result !== "rejected") return { label: "Voided", color: "default" };
    if (!r.ncr) return { label: "Voided rejection: link an NCR", color: "error" };
    return closedAfterLink(r.ncr) ? { label: "Voided rejection, NCR closed", color: "default" } : { label: "Voided rejection: NCR not yet closed", color: "error" };
  }
  if (r.status !== "complete" || !["accepted", "rejected"].includes(r.result)) return { label: "Pending", color: "warning" };
  if (r.result === "accepted") return { label: "Accepted", color: "success" };
  const retest = all.some((x) => x.weld.id === r.weld.id && x.method === r.method && x.id > r.id && x.status === "complete" && x.result === "accepted");
  if (!r.ncr) return { label: retest ? "Rejected, re-inspected: link the NCR" : "Rejected: link an NCR", color: "error" };
  if (retest) return { label: "Rejected, repaired and re-inspected", color: "success" };
  if (r.ncr.clearsAsIs) return { label: "Rejected, accepted as is (QA NCR)", color: "success" };
  return { label: r.ncr.register === "NCRS" ? "Rejected: re-inspect (a system NCR does not accept as is)" : "Rejected: NCR not yet cleared", color: "error" };
}

export default function NdeRecords() {
  const [params, setParams] = useSearchParams();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canLink = perms.includes("edit_ncr");
  const jobId = params.get("jobId") || "";
  const [jobs, setJobs] = useState([]);
  const [records, setRecords] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null);   // { record, candidates, choice, reason }

  useEffect(() => { ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || r.data?.data || [])).catch(() => setJobs([])); }, []);
  const load = useCallback(async () => {
    if (!jobId) { setRecords([]); return; }
    try { const r = await ApiCall.get(`/qdp-links/nde-records/${jobId}`); setRecords(r.data.data.records || []); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load NDE results.") }); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => records.map((r) => ({ ...r, standing: ndeStanding(r, records) })), [records]);
  const open = async (record) => {
    setMsg(null);
    try { const r = await ApiCall.get(`/qdp-links/nde/${record.id}/ncr`); setDlg({ record, candidates: r.data.data.candidates || [], choice: "", reason: "" }); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load the job's NCRs.") }); }
  };
  const save = async () => {
    const [register, id] = String(dlg.choice).split(":");
    setBusy(true);
    try {
      const r = await ApiCall.post(`/qdp-links/nde/${dlg.record.id}/ncr`, { register, ncrId: Number(id), reason: dlg.reason.trim() });
      setMsg({ severity: "success", text: r.data.message }); setDlg(null); await load();
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not linked.") }); } finally { setBusy(false); }
  };

  return (
    <>
      <Helmet><title>NDE results and NCRs | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>NDE results and NCR dispositions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A rejected result clears only by a later accepted re-inspection by the same method, or by its QA NCR dispositioned use-as-is and closed after the link.
          Link an open NCR that covers the welded mark; the link is made once, with a reason, and is never changed.
        </Typography>
        <TextField select size="small" label="Job" value={jobId} sx={{ minWidth: 280, mb: 2 }}
          onChange={(e) => { const p = new URLSearchParams(params); if (e.target.value) p.set("jobId", e.target.value); else p.delete("jobId"); setParams(p); }}>
          <MenuItem value="">Choose a job</MenuItem>
          {jobs.map((j) => <MenuItem key={j.id} value={String(j.id)}>{j.name || j.jobName || `Job ${j.id}`}</MenuItem>)}
        </TextField>
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
        {jobId && (
          <Card sx={{ p: 0, overflowX: "auto" }}>
            {rows.length === 0 ? <Typography sx={{ p: 2 }}>No NDE results recorded on this job.</Typography> : (
              <SmartTable tableId="nde-records" lockedColumns={["Mark / joint", "Result", "NCR", "Standing"]} size="small">
                <TableHead><TableRow>{["Mark / joint", "Method", "Result", "Report", "Technician", "Inspected", "NCR", "Standing", ""].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.weld.mark} {r.weld.joint || `weld ${r.weld.id}`}</TableCell>
                    <TableCell>{r.method}</TableCell>
                    <TableCell>{r.status === "complete" ? `${r.result}${r.indications ? ` (${r.indications} indications)` : ""}` : r.status}</TableCell>
                    <TableCell>{r.reportNumber || ""}</TableCell>
                    <TableCell>{r.technician || ""}</TableCell>
                    <TableCell>{day(r.inspectedDate)}</TableCell>
                    <TableCell>{r.ncr ? `${r.ncr.number} (${String(r.ncr.status).toLowerCase()}${r.ncr.disposition ? `, ${String(r.ncr.disposition).toLowerCase().replace(/_/g, " ")}` : ""})` : ""}</TableCell>
                    <TableCell><Chip size="small" label={r.standing.label} color={r.standing.color} /></TableCell>
                    <TableCell>{canLink && ["complete", "voided"].includes(r.status) && r.result === "rejected" && !r.ncr && <Button size="small" variant="outlined" onClick={() => open(r)}>Link NCR</Button>}</TableCell>
                  </TableRow>))}
                </TableBody>
              </SmartTable>
            )}
          </Card>
        )}
      </Container>
      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        {dlg && (<>
          <DialogTitle>Link the NCR that dispositions this rejected {dlg.record.method}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">{dlg.record.weld.mark} {dlg.record.weld.joint || `weld ${dlg.record.weld.id}`}, report {dlg.record.reportNumber || "not recorded"}, inspected {day(dlg.record.inspectedDate)}.</Typography>
              <TextField select size="small" label="NCR on this job" value={dlg.choice} onChange={(e) => setDlg({ ...dlg, choice: e.target.value })}>
                {dlg.candidates.length === 0 && <MenuItem value="" disabled>No open NCR on this job covers this mark. Raise one, or link it to the mark's pieces.</MenuItem>}
                {dlg.candidates.map((c) => (
                  <MenuItem key={`${c.register}:${c.id}`} value={`${c.register}:${c.id}`}>
                    {c.number} ({c.register === "ncrs" ? "system" : "QA"}, {String(c.status).toLowerCase()}{c.disposition ? `, ${String(c.disposition).toLowerCase().replace(/_/g, " ")}` : ""})
                  </MenuItem>))}
              </TextField>
              <TextField size="small" label="Why this NCR dispositions the rejection" multiline minRows={2} value={dlg.reason}
                onChange={(e) => setDlg({ ...dlg, reason: e.target.value })} helperText="At least 10 characters. Recorded with your name and the time; the link is not changed later." />
              <Box><Alert severity="info">Only open NCRs that cover this mark are listed. The link alone does not clear the rejection: it clears when the weld is re-inspected and accepted, or the QA NCR is dispositioned use-as-is and closed after this link.</Alert></Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDlg(null)}>Cancel</Button>
            <Button variant="contained" disabled={busy || !dlg.choice || dlg.reason.trim().length < 10} onClick={save}>Link NCR</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </>
  );
}
